import React, { useState, useEffect, useRef } from 'react';

/**
 * AnimatedNumber - Counts up to target value on scroll into view
 */
const AnimatedNumber = ({ value, duration = 1000, prefix = '', suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const lastValue = useRef(0);

  useEffect(() => {
    // If value changed significantly, animate to new value
    if (value !== lastValue.current) {
      const startValue = lastValue.current;
      lastValue.current = value;

      const start = performance.now();
      const animate = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        const current = startValue + (value - startValue) * eased;
        setDisplay(Math.round(current * 10) / 10); // Keep one decimal
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [value, duration]);

  // Format display value
  const formatted = Number.isInteger(display) ? display.toLocaleString() : display.toFixed(1);

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

/**
 * StatCard - V4 styled stat card with optional animated value
 *
 * @param {string} label - Stat label (e.g., "Total Revenue")
 * @param {number|string} value - Value to display (number for animation, string for static)
 * @param {string} prefix - Value prefix (e.g., "$")
 * @param {string} suffix - Value suffix (e.g., "h")
 * @param {string} change - Change indicator text (e.g., "12% from last month")
 * @param {string} changeType - 'up' | 'down' | 'neutral'
 * @param {boolean} animate - Whether to animate the value (default: true, ignored for string values)
 */
const StatCard = ({
  label,
  value,
  prefix = '',
  suffix = '',
  change,
  changeType = 'neutral',
  animate = true
}) => {
  const changeIcon = changeType === 'up' ? '↑' : changeType === 'down' ? '↓' : '';
  const isNumeric = typeof value === 'number';
  const shouldAnimate = animate && isNumeric;

  return (
    <div className="v4-stat-card">
      <span className="v4-stat-card__label">{label}</span>
      <span className="v4-stat-card__value">
        {shouldAnimate ? (
          <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
        ) : isNumeric ? (
          `${prefix}${value.toLocaleString()}${suffix}`
        ) : (
          `${prefix}${value}${suffix}`
        )}
      </span>
      {change && (
        <span className={`v4-stat-card__change v4-stat-card__change--${changeType}`}>
          {changeIcon} {change}
        </span>
      )}
    </div>
  );
};

export { AnimatedNumber };
export default StatCard;
